import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, change } from 'redux-form';
import { t } from 'i18next';
import {
  isReferenceNumberValid, isRequired, isRequiredTrimmed, isPhone, maxChar30, maxChar40,
} from '../../../../utils/validator';
import { phoneNumber } from '../../../../utils/mask';
import { UnitType } from '../../../../utils/enums';
import {
  AddItemButton,
  AdditionalInformationContainer,
  AdditionalInformationFields,
  AdditionalInformationRow1,
  AdditionalInformationRow2,
  Container,
  ItemsContainer,
  PageDescription,
  PageHeader,
  Summary,
  SummaryContainer,
  SummaryLabel,
  SummaryValue,
  Title,
  TitleContainer,
  ToggleContainer,
} from './css';
import InlineTextLink from '../../../../lib/ui/buttons/InlineTextLink';
import ToggleButton from '../../../../lib/ui/buttons/ToggleButton';
import Item from './Item';
import TextInput from '../../../../lib/ui/form_elements/TextInput';
import Tooltip from '../../../../lib/ui/form_elements/Tooltip';
import { calculateVolumeDensity } from '../../../../utils/func';

const renderItemsForm = ({
  fields,
  measurementList,
  formName,
  dispatch,
  formData,
  commoditiesList,
  fieldsShouldBeBlocked,
  avoidChanges,
}) => {
  const disableAddLineButton = measurementList && measurementList.some((m) => !m.completed);

  const addItem = (e) => {
    e.preventDefault();
    fields.push({});
  };

  const removeItem = (index) => {
    if (measurementList.length === 1) {
      dispatch(change(formName, 'measurementList', [{}]));
      return;
    }

    const newList = measurementList.filter((_, i) => i !== index);
    dispatch(
      change(
        formName,
        'measurementList',
        newList.length === 0 ? newList.push({}) : newList,
      ),
    );
  };

  return (
    <>
      {fields.map((item, index) => {
        const fieldValue = fields.get(index);
        if (!fieldValue.completed) {
          dispatch(change(formName, `measurementList[${index}].completed`, true));
        }

        const currentItem = formData.measurementList[index] || fieldValue;
        const {
          itemDangerous,
          commodityType,
          // TODO: itemLimitedQuantity temporarily removed - LQ disabled until backend (TruckMate) supports it.
          // itemLimitedQuantity,
          itemWeight,
          itemWidth,
          itemHeight,
          itemLength,
        } = currentItem;

        const isImperialUnits = formData.selectedUnits
          && formData.selectedUnits.indexOf(t('BtnImperial')) !== -1;
        const density = calculateVolumeDensity(
          isImperialUnits,
          itemLength,
          itemWidth,
          itemHeight,
          itemWeight,
        );

        return (
          <Item
            item={item}
            index={index}
            key={index}
            density={density}
            onRemove={removeItem}
            itemDangerous={itemDangerous}
            isImperialUnits={isImperialUnits}
            selectedUnits={formData.selectedUnits}
            // TODO: itemLimitedQuantity prop temporarily removed - LQ disabled until backend (TruckMate) supports it.
            // itemLimitedQuantity={itemLimitedQuantity}
            commoditiesList={commoditiesList}
            usShipment={formData.USShipment}
            fieldsShouldBeBlocked={fieldsShouldBeBlocked}
            avoidChanges={avoidChanges}
            measurementList={formData.measurementList[index]}
            dispatch={dispatch}
            formName={formName}
            commodityType={commodityType}
          />
        );
      })}
      <AddItemButton
        disabled={disableAddLineButton}
        onClick={
          fieldsShouldBeBlocked ? () => avoidChanges(t('AddItem')) : addItem
        }
      >
        {t('AddItem')}
      </AddItemButton>
    </>
  );
};

const Step2 = ({
  formData = {},
  step,
  formName,
  dispatch,
  valid,
  error,

  commoditiesList,
  fieldsShouldBeBlocked,
  avoidChanges,
  unitsList,
}) => {
  const { measurementList } = formData;

  const setMeasurementUnit = (index) => {
    dispatch(change(formName, 'measurementType', unitsList[index]));
  };

  const totalPallets = measurementList && measurementList.length > 0
    ? measurementList.reduce((acc, curr) => (curr.SkidQty ? Number(acc) + Number(curr.SkidQty) : acc), 0)
    : 0;
  const totalPieces = measurementList && measurementList.length > 0
    ? measurementList.reduce((acc, curr) => (curr.PieceQty ? Number(acc) + Number(curr.PieceQty) : acc), 0)
    : 0;

  const totalItems = measurementList && measurementList.length;
  const totalWeight = (measurementList && measurementList.length > 0
    ? measurementList
      .map((item) => parseFloat(item.itemWeight))
      .reduce((acc, curr) => acc + (curr || 0), 0)
    : 0
  ).toFixed(2);

  useEffect(() => {
    if (step === 1 && fieldsShouldBeBlocked) {
      let fieldsToRevalidate = [
        'shippingName',
        'unNumber',
        'totalQuantity',
        'numberPackRequireLabel',
      ];
      let hasDangerousGoods = false;

      measurementList.forEach((item, index) => {
        const prefix = `measurementList[${index}].`;
        if (item.itemDangerous) {
          hasDangerousGoods = true;
          fieldsToRevalidate.forEach((f) => {
            const fieldName = prefix + f;
            const field = document.getElementsByName(fieldName);
            if (field) {
              field[0].focus();
              field[0].blur();
            }
          });
        }
      });

      if (hasDangerousGoods) {
        fieldsToRevalidate = ['emergencyPhone'];
        fieldsToRevalidate.forEach((f) => {
          const field = document.getElementsByName(f);
          if (field) {
            field[0].focus();
            field[0].blur();
          }
        });
      }
    }
  }, [step]);

  if (step === 1) {
    return (
      <Container>
        <PageHeader>
          <TitleContainer>
            <Title>{t('Items')}</Title>
            <PageDescription>
              <span>
                {t('MeasurementsParagraph1')}
                {' '}
              </span>
              <InlineTextLink
                href={t('RestrictedArticlesLink')}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('RestrictedArticlesText')}
              </InlineTextLink>
              {t('MeasurementsParagraph2') !== 'MeasurementsParagraph2' && (
              <span>
                {' '}
                {t('MeasurementsParagraph2')}
                .
              </span>
              )}
            </PageDescription>
          </TitleContainer>
          <ToggleContainer>
            <Field
              name="selectedUnits"
              component={ToggleButton}
              label1={t('BtnImperial')}
              label2={t('BtnMetric')}
              callback={setMeasurementUnit}
              disabled={fieldsShouldBeBlocked || totalItems > 1}
            />
          </ToggleContainer>
        </PageHeader>
        <ItemsContainer>
          <FieldArray
            name="measurementList"
            dispatch={dispatch}
            valid={valid}
            error={error}
            formData={formData}
            formName={formName}
            measurementList={measurementList}
            component={renderItemsForm}
            commoditiesList={commoditiesList}
            fieldsShouldBeBlocked={fieldsShouldBeBlocked}
            avoidChanges={avoidChanges}
          />
        </ItemsContainer>

        <SummaryContainer>
          <Summary>
            <SummaryLabel>
              {t('Total')}
              :
            </SummaryLabel>
            <SummaryValue>
              {totalPallets}
              {' '}
              {totalPallets === 1 ? t('Pallet') : t('Pallets')}
              {' '}
              {totalPieces}
              {' '}
              {totalPieces === 1 ? t('Piece') : t('Pieces')}
            </SummaryValue>
          </Summary>

          <Summary>
            <SummaryLabel>
              {t('TotalWeight')}
              :
            </SummaryLabel>
            <SummaryValue>
              {totalWeight}
              {' '}
              {formData.selectedUnits === t(UnitType.Imperial) ? 'lb' : 'kg'}
            </SummaryValue>
          </Summary>
        </SummaryContainer>

        <AdditionalInformationContainer>
          <Title>{t('AdditionalInformation')}</Title>
          <AdditionalInformationFields>
            <AdditionalInformationRow1>
              <Field
                name="referenceNumber"
                component={TextInput}
                label={t('Reference')}
                maxLength={40}
                tooltip={
                  <Tooltip message={t('TipCreateShipmentReferenceNumber')} />
                }
                validate={[maxChar40, isReferenceNumberValid]}
              />
              <Field
                name="poNumber"
                component={TextInput}
                label={t('PurchaseOrder')}
                maxLength={40}
                validate={[maxChar40]}
                tooltip={(
                  <Tooltip
                    message={t('TipCreateShipmentPurchaseOrderNumber')}
                  />
                )}
              />
            </AdditionalInformationRow1>

            {measurementList
              && measurementList.some(({ itemDangerous }) => itemDangerous) && (
                <AdditionalInformationRow2>
                  <Field
                    name="emergencyPhone"
                    component={TextInput}
                    label={t('24hEmergencyNumber')}
                    validate={
                      measurementList.every(({ itemDangerous, itemLimitedQuantity }) => (
                        !itemDangerous || itemLimitedQuantity
                      )) ? [isPhone] : [isRequired, isPhone]
                    }
                    normalize={phoneNumber}
                  />
                  <Field
                    name="dgContactName"
                    component={TextInput}
                    label={t('DgContactName') || 'Contact Name'}
                    maxLength={30}
                    validate={
                      measurementList.every(({ itemDangerous, itemLimitedQuantity }) => (
                        !itemDangerous || itemLimitedQuantity
                      )) ? [maxChar30] : [isRequiredTrimmed, maxChar30]
                    }
                  />
                  <Field
                    name="erapReference"
                    component={TextInput}
                    maxLength={6}
                    label={t('ERAPReference')}
                    optional
                  />
                  <Field
                    name="erapPhone"
                    component={TextInput}
                    label={t('ERAPPhone')}
                    validate={[isPhone]}
                    normalize={phoneNumber}
                    optional
                  />
                </AdditionalInformationRow2>
            )}
          </AdditionalInformationFields>
        </AdditionalInformationContainer>
      </Container>
    );
  }

  return null;
};

Step2.propTypes = {
  formData: PropTypes.shape({
    measurementList: PropTypes.arrayOf(
      PropTypes.shape({
        SkidQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        PieceQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        itemWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        itemWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        itemHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        itemLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        itemDangerous: PropTypes.bool,
        completed: PropTypes.bool,
      }),
    ),
    selectedUnits: PropTypes.string,
    USShipment: PropTypes.bool,
    shipmentType: PropTypes.string,
    dgPrimaryClass: PropTypes.string,
    dgSubsidiaryClass: PropTypes.string,
    dgPackagingGroup: PropTypes.string,
    dgPackagingType: PropTypes.string,
  }),
  step: PropTypes.number,
  formName: PropTypes.string,
  dispatch: PropTypes.func,
  valid: PropTypes.bool,
  error: PropTypes.string,
  commoditiesList: PropTypes.shape({
    children: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.shape({
          value: PropTypes.string,
        }),
        children: PropTypes.arrayOf(
          PropTypes.shape({
            code: PropTypes.shape({
              value: PropTypes.string,
            }),
            value: PropTypes.string,
          }),
        ),
      }),
    ),
  }),
  fieldsShouldBeBlocked: PropTypes.bool,
  avoidChanges: PropTypes.func,
  unitsList: PropTypes.arrayOf(PropTypes.string),
};

export default Step2;
